/**
 * Visão geral — what needs attention, and how the BUs are set up.
 *
 * Every number here is derived from a list the BFF actually serves. There is no analytics
 * endpoint for What's New reach over time, so the design's "Leitores por dia" chart is **not
 * rendered** rather than filled with a plausible shape — see the module README.
 *
 * @module Pages/Dashboard
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { getTenants } from '@/Pages/BUs/BUManager/Services';
import { getSetupProgress } from '@/Pages/BUs/BUManager/Utils/buSetupSteps';
import { SetupProgress } from '@/Pages/BUs/BUManager/Components/SetupProgress/SetupProgress';
import { getTourOutcomeSummary, getTours } from '@/Pages/BUs/Tours/Services/Tours.service';
import {
  getWhatsNew,
  getWhatsNewReachSeries,
  getWhatsNewReadingSummary,
} from '@/Pages/BUs/WhatsNew/Services/WhatsNew.service';
import { WhatsNewApprovalStatus } from '@/Pages/BUs/WhatsNew/Types/WhatsNew.types';
import { getPreferencesStats } from '@/Pages/PlatformUsers/Preferences/Services/Preferences.service';
import { compareTenantSlugs, getTenantColor, getTenantIcon } from '@/Shared/brand';
import { DistributionBar } from '@/Shared/components/DistributionBar/DistributionBar';
import { MetricCard } from '@/Shared/components/MetricCard/MetricCard';
import { ReachChart } from '@/Shared/components/ReachChart/ReachChart';
import {
  formatReadingTime,
  getLiveContent,
  getStalledContent,
  getUnseenContent,
} from './Utils/overview';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const metricsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
  gap: var(--space-base);
`;

const queueStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-m);
  padding: var(--space-m) var(--space-ml);
  background: var(--gl-navy);
  border-radius: var(--radius-card);
  color: var(--gl-white);
  flex-wrap: wrap;
`;

const queueIconStyle = css`
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const queueTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const queueTitleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const queueBodyStyle = css`
  font-size: var(--font-size-xs);
  color: var(--gl-blue-25);
`;

const spacerStyle = css`
  flex: 1;
`;

const sectionTitleStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: var(--space-s);
`;

const headingStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const subheadingStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const tenantGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
  gap: var(--space-base);
`;

const tenantCardStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const tenantHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const tenantIconStyle = css`
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: var(--radius-m);
  background: var(--color-primary-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const tenantTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const tenantNameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const tenantSlugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const tenantStatsStyle = css`
  display: flex;
  gap: var(--space-ml);
  padding-bottom: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const tenantStatStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const statLabelStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const statValueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const setupRowStyle = css`
  display: flex;
  align-items: flex-end;
  gap: var(--space-m);
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const panelHeaderStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const panelTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const panelSubtitleStyle = css`
  margin: 0;
  max-width: 560px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const totalStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-end;
`;

const totalValueStyle = css`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
`;

const totalLabelStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const distributionsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: var(--space-xl);
`;

const columnStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const columnLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const alertListStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const alertRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-m);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-xs);
`;

const alertStrongStyle = css`
  font-weight: var(--font-weight-semibold);
`;

const liveRowStyle = css`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  min-width: 0;
`;

const liveValueStyle = css`
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const outcomeGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr));
  gap: var(--space-base);
`;

const noteStyle = css`
  display: flex;
  gap: var(--space-sm);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-2xs);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/** Distinct colours per series, stable across renders so a bar keeps its colour. */
const SERIES_COLORS = [
  'var(--gl-blue)',
  'var(--color-water)',
  'var(--gl-purple)',
  'var(--color-gas)',
  'var(--gl-navy-50)',
];

const Dashboard = (): JSX.Element => {
  const navigate = useNavigate();
  const { pages: privileges } = usePrivileges();

  const { data: tenants, isLoading: loadingTenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => getTenants(),
  });

  const { data: whatsNew } = useQuery({
    queryKey: ['whats-new', 'list'],
    queryFn: () => getWhatsNew({ limit: 100 }),
    enabled: privileges.BUSINESS_UNITS.WHATS_NEW.PAGE,
  });

  const { data: tours } = useQuery({
    queryKey: ['tours', 'list'],
    queryFn: () => getTours({ limit: 100 }),
    enabled: privileges.BUSINESS_UNITS.TOURS.PAGE,
  });

  const { data: preferences } = useQuery({
    queryKey: ['preferences', 'stats'],
    queryFn: () => getPreferencesStats(),
    enabled: privileges.PLATFORM_USERS.PREFERENCES.PAGE,
  });

  /*
    The three below are aggregates, one request each. Their per-item counterparts
    (`/whats-new/:id/stats`, `/tours/:id/stats`) would be one request per card.
  */
  const { data: reach } = useQuery({
    queryKey: ['whats-new', 'reach-series'],
    queryFn: () => getWhatsNewReachSeries(),
    enabled: privileges.BUSINESS_UNITS.WHATS_NEW.PAGE,
  });

  const { data: reading } = useQuery({
    queryKey: ['whats-new', 'reading-summary'],
    queryFn: () => getWhatsNewReadingSummary(),
    enabled: privileges.BUSINESS_UNITS.WHATS_NEW.PAGE,
  });

  const { data: tourOutcomes } = useQuery({
    queryKey: ['tours', 'outcome-summary'],
    queryFn: () => getTourOutcomeSummary(),
    enabled: privileges.BUSINESS_UNITS.TOURS.PAGE,
  });

  const announcements = useMemo(() => whatsNew?.data ?? [], [whatsNew]);
  const tourList = useMemo(() => tours?.data ?? [], [tours]);

  const pending = useMemo(
    () =>
      announcements.filter(item => item.approvalStatus === WhatsNewApprovalStatus.PENDING_APPROVAL),
    [announcements],
  );

  const approvedCount = announcements.filter(
    item => item.approvalStatus === WhatsNewApprovalStatus.APPROVED,
  ).length;
  const activeTours = tourList.filter(tour => tour.isActive).length;

  const orderedTenants = useMemo(
    () =>
      [...(tenants ?? [])]
        .filter(tenant => tenant.isActive)
        .sort((a, b) => compareTenantSlugs(a.slug, b.slug)),
    [tenants],
  );

  /** The oldest pending item — the one the queue banner should name. */
  const oldestPending = useMemo(
    () =>
      [...pending].sort(
        (a, b) =>
          new Date(a.submittedAt ?? a.createdAt).getTime() -
          new Date(b.submittedAt ?? b.createdAt).getTime(),
      )[0],
    [pending],
  );

  const incompleteTenants = orderedTenants.filter(tenant => !getSetupProgress(tenant).isComplete);

  const unseen = useMemo(
    () => getUnseenContent(orderedTenants, announcements, tourList),
    [orderedTenants, announcements, tourList],
  );

  const stalled = useMemo(() => getStalledContent(announcements), [announcements]);

  /** Approved announcements pinned to a version their BU has moved past — nobody can reach them. */
  const unreachable = useMemo(
    () =>
      orderedTenants.flatMap(tenant => getLiveContent(tenant, announcements, tourList).unreachable),
    [orderedTenants, announcements, tourList],
  );

  if (loadingTenants) {
    return (
      <MainContent id='dashboard-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  const themeEntries = Object.entries(preferences?.byTheme ?? {}).sort((a, b) => b[1] - a[1]);
  const languageEntries = Object.entries(preferences?.byLanguage ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <MainContent id='dashboard-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={metricsStyle}>
          <MetricCard
            id='metric-pending'
            label='Aguardando aprovação'
            value={pending.length}
            icon='Timetable.Clock'
            tone='danger'
            footnote={
              pending.length === 0
                ? 'nada na fila'
                : `em ${new Set(pending.map(item => item.tenantSlug)).size} tenant(s)`
            }
          />
          <MetricCard
            id='metric-whats-new'
            label="What's New aprovados"
            value={approvedCount}
            icon='Communication.Newspaper'
            tone='primary'
            footnote={`nos ${orderedTenants.length} tenants ativos`}
          />
          <MetricCard
            id='metric-tours'
            label='Tours ativos'
            value={activeTours}
            icon='Misc.Broadcast'
            tone='water'
            footnote={`de ${tourList.length} cadastrados`}
          />
          <MetricCard
            id='metric-setup'
            label='BUs a concluir'
            value={incompleteTenants.length}
            icon='Misc.Buildings'
            tone={incompleteTenants.length === 0 ? 'success' : 'danger'}
            footnote={
              incompleteTenants.length === 0
                ? 'todas com os 4 passos'
                : incompleteTenants.map(tenant => tenant.slug).join(', ')
            }
          />
        </div>

        {pending.length > 0 && privileges.BUSINESS_UNITS.WHATS_NEW.PAGE ? (
          <div className={queueStyle}>
            <span className={queueIconStyle}>
              <GLIcon variant='Timetable.Clock' size={18} fill='var(--gl-white)' />
            </span>
            <div className={queueTextStyle}>
              <span className={queueTitleStyle}>
                {pending.length === 1
                  ? '1 item esperando aprovação'
                  : `${pending.length} itens esperando aprovação`}
              </span>
              {oldestPending ? (
                <span className={queueBodyStyle}>
                  O mais antigo é {oldestPending.tenantSlug} v
                  {oldestPending.platformVersion?.version}, criado por{' '}
                  {oldestPending.createdBy?.name ?? 'autor desconhecido'}.
                </span>
              ) : null}
            </div>
            <div className={spacerStyle} />
            <UTButton
              fitContent
              id='dashboard-review-queue'
              size='small'
              text='Revisar fila'
              icon='Arrows.ArrowRight'
              onClick={() => {
                navigate('/business-units/whats-new');
              }}
            />
          </div>
        ) : null}

        {(unreachable.length > 0 ||
          unseen.announcements.length > 0 ||
          unseen.tours.length > 0 ||
          stalled.rejected.length > 0 ||
          stalled.drafts.length > 0) &&
        privileges.BUSINESS_UNITS.WHATS_NEW.PAGE ? (
          <section className={panelStyle} id='overview-alerts'>
            <div className={panelHeaderStyle}>
              <div>
                <h2 className={panelTitleStyle}>Precisa de atenção</h2>
                <p className={panelSubtitleStyle}>
                  Coisas que não aparecem em nenhuma contagem porque nada deu errado — elas só
                  deixaram de acontecer.
                </p>
              </div>
            </div>

            <div className={alertListStyle}>
              {unreachable.length > 0 ? (
                <div className={alertRowStyle}>
                  <GLIcon
                    variant='Notifications.WarningCircle'
                    size={16}
                    fill='var(--color-danger)'
                  />
                  <span>
                    <span className={alertStrongStyle}>
                      {unreachable.length === 1
                        ? '1 anúncio aprovado está inalcançável'
                        : `${unreachable.length} anúncios aprovados estão inalcançáveis`}
                    </span>{' '}
                    — a BU já passou da versão para a qual eles foram escritos, e a entrega exige a
                    versão exata. Ninguém vai ver, e nada no produto avisa.
                  </span>
                  <div className={spacerStyle} />
                  <UTButton
                    fitContent
                    variant='tertiary'
                    size='small'
                    text='Ver'
                    onClick={() => {
                      navigate('/business-units/whats-new');
                    }}
                  />
                </div>
              ) : null}

              {unseen.announcements.length > 0 || unseen.tours.length > 0 ? (
                <div className={alertRowStyle}>
                  <GLIcon variant='Misc.Eye' size={16} fill='var(--color-text-muted)' />
                  <span>
                    <span className={alertStrongStyle}>Publicado, mas ninguém abriu</span> —{' '}
                    {[
                      unseen.announcements.length > 0
                        ? `${unseen.announcements.length} anúncio(s)`
                        : null,
                      unseen.tours.length > 0 ? `${unseen.tours.length} tour(s)` : null,
                    ]
                      .filter(Boolean)
                      .join(' e ')}{' '}
                    estão no ar com zero visualizações. Em geral é flag desligada, versão que nunca
                    chegou ou página que não existe.
                  </span>
                </div>
              ) : null}

              {stalled.rejected.length > 0 ? (
                <div className={alertRowStyle}>
                  <GLIcon variant='Arrows.X' size={16} fill='var(--color-text-muted)' />
                  <span>
                    <span className={alertStrongStyle}>
                      {stalled.rejected.length === 1
                        ? '1 anúncio rejeitado'
                        : `${stalled.rejected.length} anúncios rejeitados`}
                    </span>
                    {stalled.oldestRejected ? (
                      <>
                        {' '}
                        — o mais antigo é {stalled.oldestRejected.tenantSlug} v
                        {stalled.oldestRejected.platformVersion?.version}. Rejeitado não volta
                        sozinho: alguém precisa corrigir e reenviar.
                      </>
                    ) : null}
                  </span>
                </div>
              ) : null}

              {stalled.drafts.length > 0 ? (
                <div className={alertRowStyle}>
                  <GLIcon
                    variant='Settings.PencilSimple'
                    size={16}
                    fill='var(--color-text-muted)'
                  />
                  <span>
                    <span className={alertStrongStyle}>
                      {stalled.drafts.length === 1
                        ? '1 rascunho sem enviar'
                        : `${stalled.drafts.length} rascunhos sem enviar`}
                    </span>{' '}
                    — nunca foram para aprovação.
                  </span>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <div className={sectionTitleStyle}>
          <h2 className={headingStyle}>Business units</h2>
          <p className={subheadingStyle}>
            O que cada tenant tem publicado e o quanto do cadastro está concluído.
          </p>
        </div>

        <div className={tenantGridStyle}>
          {orderedTenants.map(tenant => {
            const progress = getSetupProgress(tenant);
            const tenantPages = announcements.filter(item => item.tenantSlug === tenant.slug);
            const tenantTours = tourList.filter(tour => tour.tenantSlug === tenant.slug);
            const live = getLiveContent(tenant, announcements, tourList);

            return (
              <article
                key={tenant.slug}
                id={`overview-tenant-${tenant.slug}`}
                className={tenantCardStyle}
              >
                <div className={tenantHeaderStyle}>
                  <span className={tenantIconStyle}>
                    <GLIcon
                      variant={getTenantIcon(tenant)}
                      size={19}
                      fill={getTenantColor(tenant.slug)}
                    />
                  </span>
                  <div className={tenantTextStyle}>
                    <span className={tenantNameStyle}>{tenant.name || tenant.slug}</span>
                    <span className={tenantSlugStyle}>
                      {tenant.slug} · v{tenant.version}
                    </span>
                  </div>
                </div>

                <div className={tenantStatsStyle}>
                  <div className={tenantStatStyle}>
                    <span className={statLabelStyle}>What&apos;s New</span>
                    <span className={statValueStyle}>{tenantPages.length}</span>
                  </div>
                  <div className={tenantStatStyle}>
                    <span className={statLabelStyle}>Tours</span>
                    <span className={statValueStyle}>{tenantTours.length}</span>
                  </div>
                </div>

                {/*
                  The counts above are what is REGISTERED. This line is what a user of the BU
                  actually gets today — the two differ constantly, and only the second one is the
                  question anybody asks.
                */}
                <div className={liveRowStyle}>
                  <span>No ar:</span>
                  <span className={liveValueStyle}>
                    {live.announcement
                      ? `1 anúncio (v${live.announcement.platformVersion?.version})`
                      : 'nenhum anúncio'}
                    {' · '}
                    {live.activeTours === 1 ? '1 tour' : `${live.activeTours} tours`}
                  </span>
                </div>

                <div className={setupRowStyle}>
                  <SetupProgress progress={progress} showPending />
                  <div className={spacerStyle} />
                  {!progress.isComplete && privileges.BUSINESS_UNITS.PAGE ? (
                    <UTButton
                      fitContent
                      variant='tertiary'
                      size='small'
                      text='Concluir'
                      onClick={() => {
                        navigate('/business-units');
                      }}
                    />
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>

        {privileges.BUSINESS_UNITS.WHATS_NEW.PAGE && reach ? (
          <section className={panelStyle} id='overview-reach'>
            <div className={panelHeaderStyle}>
              <div>
                <h2 className={panelTitleStyle}>Leitores por dia</h2>
                <p className={panelSubtitleStyle}>
                  Pessoas distintas que abriram seu primeiro anúncio no dia, de {reach.range.from} a{' '}
                  {reach.range.to}.
                </p>
              </div>
              <div className={spacerStyle} />
              <div className={totalStyle}>
                <span className={totalValueStyle}>{reach.distinctViewers}</span>
                {/*
                  The previous period as a number instead of a second line on the chart: at these
                  volumes two interleaved series are unreadable, and this is the comparison people
                  actually want. `distinctViewers` against `previousDistinctViewers` — both counted
                  the same way, unlike `total`, which double-counts anyone who read on two days.
                */}
                <span className={totalLabelStyle}>
                  pessoas no período
                  {reach.previousDistinctViewers > 0
                    ? ` · antes: ${reach.previousDistinctViewers}`
                    : ''}
                </span>
              </div>
            </div>

            <ReachChart
              values={reach.byDay.map(point => point.viewers)}
              dates={reach.byDay.map(point => point.date)}
              label={`Leitores por dia entre ${reach.range.from} e ${reach.range.to}`}
            />

            {reading ? (
              <div className={outcomeGridStyle}>
                <div className={tenantStatStyle}>
                  <span className={statLabelStyle}>Tempo de leitura (mediana)</span>
                  <span className={statValueStyle}>
                    {formatReadingTime(reading.medianDurationMs)}
                  </span>
                </div>
                <div className={tenantStatStyle}>
                  <span className={statLabelStyle}>Leituras registradas</span>
                  <span className={statValueStyle}>{reading.totalViews}</span>
                </div>
                <div className={tenantStatStyle}>
                  <span className={statLabelStyle}>Anúncios já lidos</span>
                  <span className={statValueStyle}>{reading.announcementsWithViews}</span>
                </div>
              </div>
            ) : null}

            {reading && reading.reportedDurationCount < reading.totalViews ? (
              <div className={noteStyle}>
                <GLIcon variant='Communication.Info' size={14} fill='var(--color-text-muted)' />
                <span>
                  A mediana cobre {reading.reportedDurationCount} de {reading.totalViews} leituras —
                  as demais foram registradas antes do dashboard passar a reportar o tempo. É a
                  mediana, e não a média, porque o número mede o modal aberto: uma aba esquecida
                  puxaria a média sozinha.
                </span>
              </div>
            ) : null}
          </section>
        ) : null}

        {privileges.BUSINESS_UNITS.TOURS.PAGE && tourOutcomes ? (
          <section className={panelStyle} id='overview-tours'>
            <div className={panelHeaderStyle}>
              <div>
                <h2 className={panelTitleStyle}>Como os tours terminam</h2>
                <p className={panelSubtitleStyle}>
                  Visualizações da revisão atual de cada tour, e não tours — quem conclui é a
                  pessoa. Subir a revisão zera a contagem, que é o que a tela de tours promete.
                </p>
              </div>
              <div className={spacerStyle} />
              <div className={totalStyle}>
                <span className={totalValueStyle}>{tourOutcomes.totalViews}</span>
                <span className={totalLabelStyle}>
                  em {tourOutcomes.toursWithViews} de {tourOutcomes.totalTours} tours
                </span>
              </div>
            </div>

            <div className={outcomeGridStyle}>
              <div className={tenantStatStyle}>
                <span className={statLabelStyle}>Concluíram</span>
                <span className={statValueStyle}>{tourOutcomes.byStatus.completed}</span>
              </div>
              <div className={tenantStatStyle}>
                <span className={statLabelStyle}>Abandonaram</span>
                <span className={statValueStyle}>{tourOutcomes.byStatus.abandoned}</span>
              </div>
              <div className={tenantStatStyle}>
                <span className={statLabelStyle}>Pularam</span>
                <span className={statValueStyle}>{tourOutcomes.byStatus.skipped}</span>
              </div>
              <div className={tenantStatStyle}>
                <span className={statLabelStyle}>Sem registro</span>
                <span className={statValueStyle}>{tourOutcomes.byStatus.unknown}</span>
              </div>
            </div>

            {tourOutcomes.byStatus.unknown > 0 ? (
              <div className={noteStyle}>
                <GLIcon variant='Communication.Info' size={14} fill='var(--color-text-muted)' />
                <span>
                  &quot;Sem registro&quot; são visualizações anteriores ao dashboard passar a
                  reportar como a pessoa saiu. Significa que não sabemos — nunca que ela abandonou.
                </span>
              </div>
            ) : null}
          </section>
        ) : null}

        {privileges.PLATFORM_USERS.PREFERENCES.PAGE && preferences ? (
          <section className={panelStyle} id='overview-preferences'>
            <div className={panelHeaderStyle}>
              <div className={tenantTextStyle}>
                <h3 className={panelTitleStyle}>Preferências dos usuários da plataforma</h3>
                <p className={panelSubtitleStyle}>
                  Tema e idioma que cada usuário escolheu no dashboard. Só isso é armazenado em
                  platform_user_preference.
                </p>
              </div>
              <div className={spacerStyle} />
              <div className={totalStyle}>
                <span className={totalValueStyle}>{preferences.total}</span>
                <span className={totalLabelStyle}>usuários com preferência salva</span>
              </div>
            </div>

            <div className={distributionsStyle}>
              <div className={columnStyle}>
                <span className={columnLabelStyle}>Tema mais usado</span>
                {themeEntries.map(([theme, count], index) => (
                  <DistributionBar
                    key={theme}
                    label={theme}
                    value={count}
                    total={preferences.total}
                    color={SERIES_COLORS[index % SERIES_COLORS.length]}
                  />
                ))}
              </div>

              <div className={columnStyle}>
                <span className={columnLabelStyle}>Idioma</span>
                {languageEntries.map(([language, count], index) => (
                  <DistributionBar
                    key={language}
                    label={language}
                    value={count}
                    total={preferences.total}
                    color={SERIES_COLORS[index % SERIES_COLORS.length]}
                  />
                ))}
              </div>
            </div>

            <div className={noteStyle}>
              <GLIcon variant='Notifications.Info' size={16} fill='var(--color-text-muted)' />
              <span>
                Os percentuais são sobre quem <strong>já salvou</strong> uma preferência, não sobre
                a base de usuários do tenant — o BFF não tem esse total. &quot;unset&quot; são
                registros sem tema ou idioma definido.
              </span>
            </div>
          </section>
        ) : null}
      </div>
    </MainContent>
  );
};

export { Dashboard };
